<script>
import { mapActions, mapState, mapMutations } from "vuex";
import SideNavItem from "./SideNavItem.vue";
import SideNavHeader from "./SideNavHeader.vue";
import ProfileCard from "./ProfileCard.vue";
import OrgSwitcher from "./OrgSwitcher.vue";
import VerticalDivider from "./VerticalDivider.vue";
import {NavLinksHelper} from "@/helpers/NavLinksHelper";

const SIDE_NAV_RESIZE_DURATION = 320;
const NAV_STAGGER_STEP = 10;
const ORG_SWITCHER_DURATION = 300;

export default {
  name: "SideNav",
  components: {
    SideNavItem,
    SideNavHeader,
    ProfileCard,
    OrgSwitcher,
    VerticalDivider,
  },
  methods: {
    ...mapActions("account", {
      logout: "logout",
      getMyData: "getMyData",
      switchOrg: "switchOrg",
    }),
    ...mapMutations("global", ["toggleSideNav", "setExpanded"]),
    onNavigate() {
      if (this.isPhone()) {
        this.callSetExpandedWithTimeout(false, 400);
      }
    },
    toggleOrgSwitcher() {
      if (!this.isSideNavExpanded) {
        return;
      }

      this.setOrgSwitcherOpen(!this.isOrgSwitcherOpen);
    },
    setOrgSwitcherOpen(isOpen) {
      if (this.isOrgSwitcherOpen === isOpen) {
        return;
      }

      this.isOrgSwitcherOpen = isOpen;

      this.followIndicator(ORG_SWITCHER_DURATION);
    },
    onOrgSelected(orgId) {
      this.setOrgSwitcherOpen(false);
      this.indicatorTimers.push(setTimeout(() => {
        this.switchOrg(orgId);
      }, ORG_SWITCHER_DURATION));
    },
    onDocumentPointerDown(event) {
      if (!this.isOrgSwitcherOpen) {
        return;
      }

      if (!this.$el.contains(event.target)) {
        this.setOrgSwitcherOpen(false);
      }
    },
    onDocumentKeydown(event) {
      if (event.key === "Escape") {
        this.setOrgSwitcherOpen(false);
      }
    },
    /* La geometrie des items change a chaque frame pendant les 300 ms de
       redimensionnement de la barre : l'indicateur est donc recale frame par
       frame, c'est le seul moyen qu'il epouse la largeur en cours au lieu
       d'arriver apres coup. */
    staggerTail() {
      const items = this.$el?.querySelectorAll?.(".side-nav-header, .side-nav-item");
      return items ? items.length * NAV_STAGGER_STEP : 0;
    },
    followIndicator(duration) {
      const token = ++this.indicatorToken;
      this.isNavResizing = true;

      const total = duration + this.staggerTail();
      const startedAt = performance.now();
      const step = () => {
        if (token !== this.indicatorToken) {
          return;
        }

        this.refreshIndicator();

        if (performance.now() - startedAt < total) {
          requestAnimationFrame(step);
          return;
        }

        this.isNavResizing = false;
      };

      step();
      this.indicatorTimers.push(setTimeout(() => {
        if (token !== this.indicatorToken) {
          return;
        }

        this.isNavResizing = false;
        this.refreshIndicator();
      }, total + 60));
    },
    refreshIndicator() {
      const nav = this.$el;
      const el = this.$refs.indicator;
      if (!nav || !el) {
        return;
      }

      const target = nav.querySelector(".side-nav-item.selected");

      if (!target) {
        if (this.isIndicatorVisible) {
          this.isIndicatorVisible = false;
        }
        return;
      }

      const top = target.offsetTop;
      const left = target.offsetLeft;
      const width = target.offsetWidth;
      const height = target.offsetHeight;
      const geom = this.indicatorGeom;

      if (
        geom.top !== top ||
        geom.left !== left ||
        geom.width !== width ||
        geom.height !== height ||
        el.style.width !== `${width}px`
      ) {
        geom.top = top;
        geom.left = left;
        geom.width = width;
        geom.height = height;

        el.style.transform = `translate3d(${left}px, ${top}px, 0)`;
        el.style.width = `${width}px`;
        el.style.height = `${height}px`;
      }

      if (!this.isIndicatorVisible) {
        this.isIndicatorVisible = true;
      }
    },
    indexNavLabels() {
      if (!this.$el || !this.$el.querySelectorAll) {
        return;
      }

      this.$el
        .querySelectorAll(".side-nav-header, .side-nav-item")
        .forEach((el, index) => {
          el.style.setProperty("--stagger", index);
          const text = el.querySelector(".text");
          if (text) {
            text.style.setProperty("--stagger", index);
          }
        });
    },
    observeNavGeometry() {
      if (typeof ResizeObserver === "undefined") {
        return;
      }

      this.geometryObserver = new ResizeObserver(() => {
        if (this.isNavResizing) {
          return;
        }
        this.refreshIndicator();
      });
      this.geometryObserver.observe(this.$el);
      this.$el.querySelectorAll(".side-nav-container").forEach((container) => {
        this.geometryObserver.observe(container);
      });
    },
    clearIndicatorTimers() {
      this.indicatorTimers.forEach(clearTimeout);
      this.indicatorTimers = [];
    },
    nextFrame(callback) {
      let done = false;
      const run = () => {
        if (done) {
          return;
        }
        done = true;
        callback();
      };

      requestAnimationFrame(run);
      setTimeout(run, 40);
    },
    clearSideNavTimers() {
      if (this.sideNavOpenTimer) {
        clearTimeout(this.sideNavOpenTimer);
        this.sideNavOpenTimer = null;
      }
      if (this.sideNavCloseTimer) {
        clearTimeout(this.sideNavCloseTimer);
        this.sideNavCloseTimer = null;
      }
    },
    callSetExpanded(value) {
      this.setExpanded(value);
    },
    callSetExpandedWithTimeout(value, duration) {
      const parentThis = this;
      setTimeout(function () {
        parentThis.setExpanded(value);
      }, duration);
    },
    allowedGroup(groupName) {
      return NavLinksHelper.group(groupName)
          .filter((link) => NavLinksHelper.isAllowed(link, this.currentUser, this.isPhone()));
    },
    contentWidth(element) {
      const style = getComputedStyle(element);
      const width = parseFloat(style.width) || 0;
      if (style.boxSizing !== "border-box") return width;
      const edges = ["paddingLeft", "paddingRight", "borderLeftWidth", "borderRightWidth"]
          .reduce((sum, key) => sum + (parseFloat(style[key]) || 0), 0);
      return width - edges;
    },
    onNavEnter() {
      if (this.isNavInitializing || this.isPhone()) {
        return;
      }
      this.clearSideNavTimers();
      this.sideNavOpenTimer = setTimeout(() => {
        this.sideNavOpenTimer = null;
        this.callSetExpanded(true);
      }, this.SIDE_NAV_OPEN_DELAY);
    },
    onNavLeave() {
      if (this.isNavInitializing || this.isPhone() || this.isSideNavPinned) {
        return;
      }
      this.clearSideNavTimers();
      this.sideNavCloseTimer = setTimeout(() => {
        this.sideNavCloseTimer = null;
        this.setExpanded(false);
      }, this.SIDE_NAV_CLOSE_DELAY);
    },
    measureExpandedWidth() {
      const sideNav = this.$el;
      if (!sideNav) {
        return;
      }

      const wasCollapsed = sideNav.classList.contains("collapsed");
      const inlineWidth = sideNav.style.width;

      sideNav.classList.add("no-transition");
      sideNav.classList.remove("collapsed");
      sideNav.style.width = "";

      this.sideNavWidth = this.contentWidth(sideNav);

      if (wasCollapsed) {
        sideNav.classList.add("collapsed");
        sideNav.style.width = inlineWidth;
      } else {
        sideNav.style.width = this.sideNavWidth + "px";
      }

      this.nextFrame(() => {
        if (!this.isNavInitializing) {
          sideNav.classList.remove("no-transition");
        }
      });
    }
  },
  computed: {
    ...mapState("global", ["isSideNavExpanded"]),
    ...mapState("account", {currentUser: "user",}),
    ...mapState("preferences", {preferenceValues: "values"}),
    organizationName() {
      return this.currentUser?.organization?.name || "";
    },
    organizationTitle() {
      const name = this.organizationName;
      return name.length > 10 ? `${name.slice(0, 10)}...` : name;
    },
    organizationId() {
      return this.currentUser?.organization?.orgId || "";
    },
    manageableOrganizations() {
      return this.currentUser?.managedOrganizations || [];
    },
    dashboardLinks() {
      return this.allowedGroup("dashboardLinks");
    },
    analyticsLinks() {
      return this.allowedGroup("analyticsLinks");
    },
    settingsLinks() {
      return this.allowedGroup("settingsLinks");
    },
    superUserLinks() {
      return this.allowedGroup("superUserLinks");
    },
    isSideNavPinned() {
      return !this.isPhone() && this.preferenceValues.sideNavAlwaysExpanded === true;
    },
    favoriteLinks() {
      const ids = this.preferenceValues.favoriteLinks || [];
      const available = [
        ...this.filteredDashboardLinks,
        ...this.analyticsLinks,
        ...this.settingsLinks,
        ...this.superUserLinks,
      ];
      return ids
          .map((id) => available.find((link) => link.id === id))
          .filter((link) => link && link.page && !link.hidden);
    },
    filteredDashboardLinks() {
      if(!this.isBlackbox()) {
        return this.dashboardLinks;
      } else {
        const excludedPages = ["/invoice", "/promo", "/pickup"];
        return this.dashboardLinks.filter(link => !excludedPages.includes(link.page));
      }
    }
  },
  created() {
    this.geometryObserver = null;
    this.indicatorGeom = { top: 0, left: 0, width: 0, height: 0 };
    this.SIDE_NAV_OPEN_DELAY = 80;
    this.SIDE_NAV_CLOSE_DELAY = 300;
  },
  data() {
    return {
      sideNavWidth: 0,
      sideNavWidthCollapsed: 0,
      sideNavOpenTimer: null,
      indicatorTimers: [],
      isNavResizing: false,
      indicatorToken: 0,
      isOrgSwitcherOpen: false,
      isIndicatorVisible: false,
      sideNavCloseTimer: null,
    };
  },
  mounted() {
    const sideNav = this.$el;

    sideNav.classList.add("no-transition");
    this.sideNavWidth = this.contentWidth(sideNav);
    sideNav.classList.add("collapsed");

    this.isNavInitializing = true;

    window.addEventListener("resize", this.refreshIndicator);
    document.addEventListener("pointerdown", this.onDocumentPointerDown);
    document.addEventListener("keydown", this.onDocumentKeydown);
    this.$nextTick(() => {
      this.indexNavLabels();
      this.refreshIndicator();
      this.observeNavGeometry();
    });

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => this.measureExpandedWidth());
    }

    setTimeout(() => {
      this.sideNavWidthCollapsed = this.contentWidth(sideNav);
      sideNav.style.width = this.sideNavWidthCollapsed + "px";
      this.callSetExpanded(this.isSideNavPinned);
      this.isNavInitializing = false;
      this.refreshIndicator();
      this.nextFrame(() => {
        sideNav.classList.remove("no-transition");
      });
    }, 350);

    sideNav.addEventListener("mouseenter", this.onNavEnter);
    sideNav.addEventListener("mouseleave", this.onNavLeave);

  },

  beforeUnmount() {
    this.clearSideNavTimers();
    this.clearIndicatorTimers();
    if (this.geometryObserver) {
      this.geometryObserver.disconnect();
      this.geometryObserver = null;
    }
    window.removeEventListener("resize", this.refreshIndicator);
    document.removeEventListener("pointerdown", this.onDocumentPointerDown);
    document.removeEventListener("keydown", this.onDocumentKeydown);
    this.$el.removeEventListener("mouseenter", this.onNavEnter);
    this.$el.removeEventListener("mouseleave", this.onNavLeave);
  },

  watch: {
    currentUser() {
      this.$nextTick(() => {
        this.measureExpandedWidth();
        this.indexNavLabels();
        this.refreshIndicator();
      });
    },
    favoriteLinks() {
      this.$nextTick(() => {
        this.indexNavLabels();
        this.refreshIndicator();
      });
    },
    isSideNavPinned(isPinned) {
      this.clearSideNavTimers();
      this.setExpanded(isPinned);
    },
    $route() {
      this.$nextTick(() => {
        this.indexNavLabels();
        this.refreshIndicator();
      });
    },
    isSideNavExpanded(newState) {
      if (!newState) {
        this.setOrgSwitcherOpen(false);
      }

      this.clearIndicatorTimers();
      this.indexNavLabels();
      this.followIndicator(SIDE_NAV_RESIZE_DURATION);

      const sideNav = this.$el;

      if (newState) {
        sideNav.classList.remove("collapsed");
        sideNav.style.width = this.sideNavWidth + "px";
      } else {
        sideNav.classList.add("collapsed");
        sideNav.style.width = this.sideNavWidthCollapsed + "px";
      }
    },
  },
};
</script>

<template>
  <div>
    <div
      ref="indicator"
      class="nav-indicator"
      :class="{ 'is-visible': isIndicatorVisible, 'is-static': isNavResizing }"
    ></div>

    <profile-card
      img="logo.svg"
      :is-expanded="isSideNavExpanded"
      :is-orgs-open="isOrgSwitcherOpen"
      :title="organizationTitle"
      :subtitle="organizationId"
      :manageable-orgs="manageableOrganizations"
      @toggle-orgs="toggleOrgSwitcher"
    />

    <org-switcher
      :orgs="manageableOrganizations"
      :current-org-id="organizationId"
      :open="isOrgSwitcherOpen"
      @select="onOrgSelected"
      @close="setOrgSwitcherOpen(false)"
    />
    <vertical-divider />
    <div class="side-nav-container is-first" v-if="favoriteLinks.length > 0">
      <side-nav-header :text="$t('preferencesPage.favorites')" />
      <side-nav-item
        v-for="favoriteLink in favoriteLinks"
        :key="`favorite-${favoriteLink.page}`"
        @navigate="onNavigate"
        :text="$t(favoriteLink.text)"
        :icon="favoriteLink.icon"
        :page="favoriteLink.page"
      />
    </div>
    <div class="side-nav-container" :class="{ 'is-first': favoriteLinks.length === 0 }">
      <side-nav-header :text="$t('navBar.dashboard')" />
      <side-nav-item
        v-for="dashboardLink in filteredDashboardLinks"
        :key="dashboardLink.page"
        @navigate="onNavigate"
        :text="$t(dashboardLink.text)"
        :icon="dashboardLink.icon"
        :page="dashboardLink.page"
        :hidden="dashboardLink.hidden"
      />
    </div>
    <vertical-divider v-if="analyticsLinks.length > 0" />
    <div class="side-nav-container" v-if="analyticsLinks.length > 0">
      <side-nav-header
        :text="$t('navBar.analytics')"
        v-if="analyticsLinks.length > 0"
      />
      <side-nav-item
        v-for="analyticsLink in analyticsLinks"
        :key="analyticsLink.page"
        @navigate="onNavigate"
        :text="$t(analyticsLink.text)"
        :icon="analyticsLink.icon"
        :page="analyticsLink.page"
        :hidden="analyticsLink.hidden"
      />
    </div>
    <vertical-divider v-if="settingsLinks.length > 0" />
    <div class="side-nav-container" v-if="settingsLinks.length > 0">
      <side-nav-header
        :text="$t('navBar.account')"
        v-if="settingsLinks.length > 0"
      />

      <side-nav-item
        v-for="settingsLink in settingsLinks"
        :key="settingsLink.page"
        @navigate="onNavigate"
        :text="$t(settingsLink.text)"
        :icon="settingsLink.icon"
        :page="settingsLink.page"
        :hidden="settingsLink.hidden"
      />
    </div>
    <vertical-divider v-if="superUserLinks.length > 0" />
    <div class="side-nav-container" v-if="superUserLinks.length > 0">
      <side-nav-header
          :text="$t('navBar.superUser')"
          v-if="superUserLinks.length > 0"
      />

      <side-nav-item
          v-for="superLink in superUserLinks"
          :key="superLink.page"
          @navigate="onNavigate"
          :text="$t(superLink.text)"
          :icon="superLink.icon"
          :page="superLink.page"
          :hidden="superLink.hidden"
      />
    </div>
    <div class="side-nav-container bottom">
      <vertical-divider />
      <side-nav-item
        @click="logout()"
        :text="$t('navBar.logout')"
        icon="log-out.svg"
      />
    </div>
  </div>
</template>
<style lang="scss" scoped>
.bottom {
  margin-top: auto !important;
}
</style>
